import { createMemo, createUniqueId, type JSX, onCleanup, onMount, Show } from 'solid-js';
import { t } from '../../i18n';
import { useAcceptedMoves } from '../../lib/accepted-move';
import type { ApiBlocker } from '../../lib/blockers';
import { createCardAnswer, type SendAnswer } from '../../lib/card-answer';
import { createCardControls } from '../../lib/card-controls';
import { CardControlsContext, type CardControlsHandle } from '../../lib/card-controls-context';
import { CardMoveContext } from '../../lib/card-move';
import { createEpicControls } from '../../lib/epic-controls';
import type { UtilBox } from '../../lib/fleetUtilization';
import { usePageGuard } from '../../lib/flood-guard-context';
import type { KeyRow } from '../../lib/keys-types';
import { type CardEpic, type CardView, cardView } from '../../lib/machine-card';
import { boxJobs, jobState } from '../../lib/machine-jobs';
import type { MachineHandlers } from '../../lib/machine-panel';
import { type MovePage, useMovePage } from '../../lib/move-page';
import type { SendWorkPage } from '../../lib/send-work-page';
import { CapabilitiesZone } from './CapabilitiesZone';
import { CardBanner } from './CardBanner';
import { CardFooter } from './CardFooter';
import { DropTarget } from './DropTarget';
import { MissionCardHeader } from './MissionCardHeader';
import { MissionZone } from './MissionZone';
import { MoveWorkPanel } from './MoveWorkPanel';
import { type NowSend, type NowWork, NowZone } from './NowZone';
import { ResourcesZone } from './ResourcesZone';
import { SendNote } from './SendNote';
import { SendWork } from './SendWork';
import { SimplifiedCard } from './SimplifiedCard';

// One machine in the Cards view (#6785, epic #6552): the v2 Mission Panel card, built to
// `docs/design/fleet-redesign/prototype.html` (`window.MissionDeck`) under ruling V9, on
// today's data (owner ruling G-3). Presenter: every rule is `cardView`'s
// (`lib/machine-card.ts`); this lays the zones out. Full: header, the loud banner,
// Resources, Now, Mission, Capabilities and the footer. Simplified: header and four rows.
//
// EVERY ACTION OPENS THE PANEL BUT ONE: the name, the state's action button and the menu's
// panel items go through the page's one opener (`lib/machine-panel.ts`); an idle card's
// "Assign work" opens Send work instead (#6770). A member, a viewer or another org's machine
// gets no write anywhere (owner rulings G21, G23), its idle action reading "Open". The writes
// on the card itself are an admin's "Answer here" on a Parked prose question (#6726), whose
// box and note are this card's (`createCardAnswer`), so the note outlives the row it
// answered, and Send work (#6770): four entry points (Now's "Send", the simplified card's
// "Send work", the menu's "Send work", idle's "Assign work") open ONE form, held once per page
// (`SendWorkPage.openId`), after Now on a full card and below the buttons on a simplified one.
//
// THE JOB AND EPIC CONTROLS (#6753): under the page's ONE flood guard (`usePageGuard`, made by
// `FleetMachines`), the card makes its controller (`createCardControls`, `createEpicControls`)
// and provides it to its zones (`CardControlsContext`), on for an admin on an own-org machine
// only (`!view().readOnly`). No guard (a card mounted outside the page) shows no control. The
// epics the card is read with are FOLDED with the controls' answers and waiting changes, so
// "Paused by you" shows at once. A Stop confirm outlines the card red, flashing once.
//
// MOVING WORK (#6771): under the page's move controller (`useMovePage`, made by `FleetMachines`)
// the card provides its move handle (`CardMoveContext`) on the same gate as the controls, so
// its rows carry grips and Move buttons and the card is a drop target (`DropTarget`) for work
// dragged from another machine; a member, a viewer or another org's machine gets none.
//
// MOVE WORK (#6772): on the same gate, and only online, with work on it, in an org of two
// machines or more, Now's "Move", the simplified card's "Move work" and the menu's Move work
// and Drain machine open the card's Move work panel (`MoveWorkPanel`), one per page, after Now.
// Opening it closes Send work and Send work closes it; on a simplified card it opens the full
// card. Closed, focus goes back to Now's Move button, then to the moved row on its new card.

/** What the card's controls write with (#6753): the session token, the re-read, the keys. */
export type CardWrites = {
  readonly token: string | null;
  readonly onWritten: () => void;
  /** The account's keys for the epic model picker; undefined until read. */
  readonly keys: () => readonly KeyRow[] | undefined;
  /** When the fetch behind the shown fleet read started (`read_started_ms`; 0 when unknown). */
  readonly readAt: () => number;
};

export type MissionCardProps = MachineHandlers & {
  readonly box: UtilBox;
  /** The whole fleet read: "Running hot" names the own-org machine with the most room. */
  readonly boxes: readonly UtilBox[];
  /** This org's open blockers by task (`blockersByTask`). */
  readonly byTask: ReadonlyMap<string, readonly ApiBlocker[]>;
  /** `canAdminWrite` on a settled role (owner ruling G21). */
  readonly canWrite: boolean;
  /** The jobs' epics (`cardEpicsFromJobs`, #6745), or null when not reported. */
  readonly epics: readonly CardEpic[] | null;
  readonly simplified: boolean;
  /** The page's answer call (`useBlockerAnswer`): answer, then re-read the blockers. */
  readonly onAnswer: SendAnswer;
  readonly onSimplify: (simplified: boolean) => void;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
  /** The page's Send work controller (#6770); absent, the card offers no Send work. */
  readonly send?: SendWorkPage | undefined;
  /** What the job and epic controls write with (#6753); absent, the controls cannot send. */
  readonly writes?: CardWrites | undefined;
};

export function MissionCard(props: MissionCardProps): JSX.Element {
  const bodyId = createUniqueId();
  const controls = cardControls(props);
  const movePage = useMovePage();
  const view = createMemo<CardView>(() =>
    cardView(
      props.box,
      {
        boxes: props.boxes,
        byTask: props.byTask,
        canWrite: props.canWrite,
        nowMs: Date.now(),
        epics: controls === null ? props.epics : controls.epics.fold(props.epics),
      },
      t,
    ),
  );
  const writable = (): boolean => !view().readOnly && props.writes !== undefined;
  const open = (): void => props.onManage(props.box);
  let article: HTMLElement | undefined;
  // Send work (#6770): only an admin on an own-org machine, never on an offline one.
  const page = (): SendWorkPage | null =>
    props.send === undefined || view().readOnly ? null : props.send;
  const offline = (): boolean => view().state === 'offline';
  const formOpen = (): boolean => page()?.openId() === props.box.id;
  const openForm = (): void => {
    if (offline()) return;
    movePage?.closePanel();
    page()?.open(props.box.id);
  };
  const closeForm = (): void => {
    page()?.close();
    article?.querySelector<HTMLElement>('.mission-send__open')?.focus();
  };
  const nowSend = (): NowSend | undefined => {
    const p = page();
    if (p === null) return undefined;
    return {
      offline: offline(),
      expanded: formOpen(),
      onOpen: () => (formOpen() ? closeForm() : openForm()),
      starting: p.starting(props.box),
    };
  };
  const work = moveWork(props, movePage, writable, offline, {
    closeSend: () => page()?.close(),
    // Back to Now's Move button, or, once nothing is left to move here, the card's name.
    focusOpen: () =>
      (
        article?.querySelector<HTMLElement>('[data-move-work]') ??
        article?.querySelector<HTMLElement>('.machine-card__name')
      )?.focus(),
  });
  const onAction = (): void => {
    if (view().state === 'idle' && page() !== null) openForm();
    else open();
  };
  const sendArea = (): JSX.Element => (
    <Show when={page()}>
      {(p) => (
        <>
          <SendNote boxId={props.box.id} page={p()} />
          <Show when={formOpen()}>
            <SendWork box={props.box} state={view().state} page={p()} onClose={closeForm} />
          </Show>
        </>
      )}
    </Show>
  );
  // A note that still holds focus as it expires gives it to the card's name, the card's
  // first control, rather than let it fall to the page when the note leaves.
  const handBack = (): void => {
    const note = article?.querySelector('.mission-zone__note');
    if (note?.contains(document.activeElement) !== true) return;
    article?.querySelector<HTMLButtonElement>('.machine-card__name')?.focus();
  };
  const answer = createCardAnswer((id, reply) => props.onAnswer(id, reply), {
    onNoteExpire: handBack,
  });
  const body = (): JSX.Element => (
    <li class="machine-card" data-state={view().state}>
      <article
        ref={article}
        class="mission-card"
        classList={{
          'mission-card--simplified': props.simplified,
          'mission-card--alert': controls?.card.ask() != null,
          'mission-card--flash': controls?.card.flash() === true,
        }}
        aria-label={t('fleet.machines.card_label', { name: props.box.display_name })}
        data-move-card={props.box.id}
      >
        <MissionCardHeader
          box={props.box}
          view={view()}
          canWrite={props.canWrite}
          simplified={props.simplified}
          bodyId={bodyId}
          onSimplify={props.onSimplify}
          notify={props.notify}
          onSendWork={page() === null ? undefined : openForm}
          onMoveWork={movePage === null ? undefined : (_box, drain) => work.open(drain)}
          onManage={props.onManage}
          onRename={props.onRename}
          onRemove={props.onRemove}
          onDrain={props.onDrain}
          onUndrain={props.onUndrain}
        />
        <div id={bodyId} class="mission-card__body">
          <Show
            when={!props.simplified}
            fallback={
              <SimplifiedCard view={view()} onAction={onAction} send={nowSend()} work={work.now()}>
                {sendArea()}
              </SimplifiedCard>
            }
          >
            <Show when={view().loud}>
              <CardBanner view={view()} onAction={onAction} />
            </Show>
            <ResourcesZone box={props.box} view={view()} />
            <NowZone
              box={props.box}
              view={view()}
              nowMs={Date.now()}
              send={nowSend()}
              work={work.now()}
            />
            {sendArea()}
            <Show when={movePage !== null ? work.panel() : null} keyed>
              {(p) => (
                <MoveWorkPanel
                  page={movePage as MovePage}
                  box={props.box}
                  drain={p.drain}
                  onClose={work.close}
                />
              )}
            </Show>
            <Show when={view().mission !== null || answer.note() !== null || work.epicNote()}>
              <MissionZone mission={view().mission} answer={answer} state={view().state} />
            </Show>
            <CapabilitiesZone view={view()} />
            <CardFooter view={view()} onAction={onAction} />
          </Show>
        </div>
        <Show when={controls !== null && writable()}>
          <p class="sr-only" role="status">
            {controls?.card.live()}
          </p>
        </Show>
        <Show when={movePage !== null && controls !== null && writable()}>
          <DropTarget />
        </Show>
      </article>
    </li>
  );
  if (controls === null) return body();
  const handle: CardControlsHandle = {
    ...controls,
    on: writable,
    keys: () => props.writes?.keys(),
  };
  if (movePage === null)
    return <CardControlsContext.Provider value={handle}>{body()}</CardControlsContext.Provider>;
  const move = {
    page: movePage,
    box: () => props.box,
    byTask: () => props.byTask,
    on: writable,
  };
  return (
    <CardControlsContext.Provider value={handle}>
      <CardMoveContext.Provider value={move}>{body()}</CardMoveContext.Provider>
    </CardControlsContext.Provider>
  );
}

/**
 * The card's Move work (#6772): where its buttons show, the open panel, and opening and
 * closing it. It opens only for a writable, online card with work on it, in an org of two
 * machines or more; the panel's own why line says why a move cannot go.
 */
function moveWork(
  props: MissionCardProps,
  movePage: MovePage | null,
  writable: () => boolean,
  offline: () => boolean,
  o: { readonly closeSend: () => void; readonly focusOpen: () => void },
) {
  const accepted = useAcceptedMoves();
  if (movePage !== null) {
    onMount(() => onCleanup(movePage.renderCard(props.box.id)));
  }
  const can = (): boolean =>
    movePage !== null &&
    writable() &&
    !offline() &&
    boxJobs(props.box).some((j) => jobState(j) !== 'last_known') &&
    (movePage?.ownMachines() ?? 0) >= 2;
  // Opening needs `can()`; an OPEN panel stays until it closes, so a re-read after its own 201
  // (the last job left this machine) does not unmount it inside "Transferring…". It still goes
  // with the card's write access.
  const panel = () => {
    const p = movePage?.panel() ?? null;
    return p !== null && p.boxId === props.box.id && writable() ? p : null;
  };
  const open = (drain: boolean): void => {
    if (movePage === null || !can()) return;
    o.closeSend();
    if (props.simplified) props.onSimplify(false);
    movePage.openPanel(props.box.id, drain);
  };
  return {
    panel,
    open,
    close: (_movedKey: string | null): void => {
      movePage?.closePanel();
      o.focusOpen();
    },
    now: (): NowWork | undefined =>
      can()
        ? {
            expanded: panel() !== null,
            onOpen: () => (panel() === null ? open(false) : movePage?.closePanel()),
          }
        : undefined,
    /** An epic move's note shows in Mission: the zone shows for it. */
    epicNote: (): boolean => {
      const n = accepted?.note() ?? null;
      return n !== null && n.zone === 'mission' && n.boxId === props.box.id;
    },
  };
}

/** The card's controller under the page's guard, or null outside a Fleet page. */
function cardControls(props: MissionCardProps): Pick<CardControlsHandle, 'card' | 'epics'> | null {
  const page = usePageGuard();
  if (page === null) return null;
  const card = createCardControls({
    page,
    box: () => props.box,
    get token() {
      return props.writes?.token ?? null;
    },
    onWritten: () => props.writes?.onWritten(),
    readAt: () => props.writes?.readAt() ?? 0,
  });
  return { card, epics: createEpicControls(card) };
}
